// src/pages/banner-module/ReadBanners.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudTable from "../../../components/dashboard/table/CrudTable";
import bannerService from "../../../services/bannerService";
import { useCrud } from "../../../hooks/useCrud";

const localCacheKey = "bannerReadCache";

export default function ReadBanners() {
  const navigate = useNavigate();
  const { deleteItem } = useCrud("backend/banners");
const [searchText, setSearchText] = useState("");
  const [cachedData, setCachedData] = useState(() => {
    const cache = localStorage.getItem(localCacheKey);
    return cache ? JSON.parse(cache) : null;
  });

  const [localData, setLocalData] = useState(cachedData?.banners || []);
  const [loading, setLoading] = useState(!cachedData);

 const showSwal = useCallback((title, text, icon) => {
  Swal.fire({
    title,
    text,
    icon,
    confirmButtonText: "OK",
    buttonsStyling: false,
    customClass: {
      confirmButton: "btn btn-primary",
    },
    didOpen: () => {
      const btn = Swal.getConfirmButton();

      btn.style.backgroundColor = "#214b7a";
      btn.style.borderColor = "#214b7a";
      btn.style.color = "#fff";
    },
  });
}, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const bannerRes = await bannerService.getAllBanners();
      const banners = bannerRes.data.data || bannerRes.data;

      const newCache = { banners };
      const existingCache = localStorage.getItem(localCacheKey);
      const parsedCache = existingCache ? JSON.parse(existingCache) : null;

      if (!parsedCache || JSON.stringify(parsedCache.banners) !== JSON.stringify(banners)) {
        localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        setCachedData(newCache);
      }

      setLocalData(banners);
    } catch (error) {
      console.error("Failed to load banners:", error);
      showSwal("Error", "Failed to load banner data.", "error");
    } finally {
      setLoading(false);
    }
  }, [showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.banners);
      setLoading(false);
      loadData(); // background refresh
    } else {
      loadData();
    }
  }, [cachedData, loadData]);

  const updateLocalDataCache = (updatedBanners) => {
    const newCache = { banners: updatedBanners };
    setLocalData(updatedBanners);
    setCachedData(newCache);
    localStorage.setItem(localCacheKey, JSON.stringify(newCache));
  };

  const handleDelete = async (id) => {
  const confirm = await Swal.fire({
  title: "Are you sure?",
  text: "This action cannot be undone!",
  icon: "warning",
  showCancelButton: true,
  confirmButtonText: "Yes, delete it!",
  cancelButtonText: "Cancel",

  // ✅ ONLY this changes "Yes, delete it!" button color
  confirmButtonColor: "#214b7a",

  // optional cancel color (leave if you want default red)
  cancelButtonColor: "#dc3545",
});

    if (confirm.isConfirmed) {
      try {
        await deleteItem(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Banner has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error);
        showSwal("Error", "Failed to delete banner.", "error");
      }
    }
  };

  // ✅ Optional: handle active/inactive toggle
  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        await bannerService.changeBannerStatus(item.id, !item.status);
        const updated = localData.map((banner) =>
          banner.id === item.id ? { ...banner, status: !item.status } : banner
        );
        updateLocalDataCache(updated);
        showSwal("Success!", "Banner status updated.", "success");
      } catch (error) {
        console.error("Status update failed:", error);
        showSwal("Error!", "Failed to update banner status.", "error");
      }
    },
    [localData, showSwal]
  );

  return (
    <DashboardWrapper>


      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/slider/create")}
             style={{
    backgroundColor: "#214b7a",
    borderColor: "#214b7a",
    color: "#fff"
  }}
            
          >
            Create Slider
          </button>
        </div>

        <div className="card shadow">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
       <h4 className="mb-0">Sliders</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search products..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          <div className="card-body">

          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <CrudTable
              data={localData}
              onEdit={(row) => navigate(`/admin/slider/edit/${row.id}`)}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
                searchText={searchText}
              columns={["id", "banner_title", "banner", "status"]}
            />
          )}
        </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
